// hooks/useDepartmentManagementLogic.js
import Icon from 'components/appIcon';
import Button from 'components/ui/button';
import { useState, useEffect } from 'react';
import departmentApi from 'services/api/serviceApi';

const useDepartmentManagementLogic = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    department: null,
    mode: "add",
  });
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  // Notification functions
  const closeNotification = () => {
    setNotification(null);
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  // Data loading function
  const loadDepartments = async () => {
    try {
      setLoading(true);
      const departmentsData = await departmentApi.getDepartments();
      setDepartments(departmentsData);
    } catch (error) {
      console.error("Error fetching departments:", error);
      showNotification("Failed to fetch departments", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadDepartments();
  }, []);

  // Form handling functions
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.name.trim()) validationErrors.name = "Department name is required";
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showNotification("Please fix errors before saving", "error");
      return;
    }

    try {
      if (modalState.mode === "add") {
        await departmentApi.createDepartment(formData);
        showNotification("Department created successfully", "success");
      } else {
        await departmentApi.updateDepartment(modalState.department.id, formData);
        showNotification("Department updated successfully", "success");
      }
      setModalState({ isOpen: false, department: null, mode: "add" });
      loadDepartments();
    } catch (error) {
      console.error("Error saving department:", error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message||  "Failed to save department";
      showNotification(errorMessage, "error");
    }
  };

  const handleAddDepartment = () => {
    setFormData({
      name: "",
      description: "",
      is_active: true,
    });
    setErrors({});
    setModalState({ isOpen: true, department: null, mode: "add" });
  };

  const handleEditDepartment = (department) => {
    setFormData({
      name: department.name,
      description: department.description || "",
      is_active: department.is_active,
    });
    setErrors({});
    setModalState({ isOpen: true, department, mode: "edit" });
  };

  const handleDeleteDepartment = async (department) => {
    if (window.confirm(`Are you sure you want to delete "${department.name}"?`)) {
      try {
        await departmentApi.deleteDepartment(department.id);
        showNotification("Department deleted successfully", "success");
        loadDepartments();
      } catch (error) {
        console.error("Error deleting department:", error);
        const errorMessage = error.response?.data?.detail || "Failed to delete department";
        showNotification(errorMessage, "error");
      }
    }
  };

  // Table columns definition
  const columns = [
    {
      header: "Name",
      accessor: "name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Description",
      accessor: "description",
      cell: (row) => <span>{row.description || "No description"}</span>,
    },
    {
      header: "Status",
      accessor: "is_active",
      cell: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.is_active 
            ? "bg-green-100 text-green-800" 
            : "bg-red-100 text-red-800"
        }`}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditDepartment(row)}
            className="p-1.5"
          >
            <Icon name="Edit" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteDepartment(row)}
            className="p-1.5 text-red-500 hover:text-red-700"
          >
            <Icon name="Trash" size={16} />
          </Button>
        </div>
      ),
    },
  ];

  return {
    // State
    isNavCollapsed,
    departments,
    loading,
    modalState,
    notification,
    formData,
    errors,
    columns,
    
    // State setters
    setIsNavCollapsed,
    setModalState,
    
    // Functions
    closeNotification,
    showNotification,
    loadDepartments,
    handleInputChange,
    validateForm,
    handleSave,
    handleAddDepartment,
    handleEditDepartment,
    handleDeleteDepartment
  };
};

export default useDepartmentManagementLogic;